import Pagination, { createPaginationPages } from 'components/Pagination';
import React, { useEffect, useRef, useState } from 'react';

import DesignerGridLayout from 'components/pages/designers/DesignerGridLayout';
import Hero from 'components/pages/category/Hero';
import { ListingWrapper } from 'views/Category';
import NoResultComponent from 'components/NoResultComponent';
import PropTypes from 'prop-types';
import { Search } from 'libs/Algolia-v2';
import { extractQueryParams } from 'utils/query';
import { scrollTo } from 'utils/scrollFunctions';
import transformDesignerCategory from 'utils/dataTransformers/transformDesignerCategory';
import transformFlexibleBackground from 'libs/wordpress/utils/transformFlexibleBackground';
import useAboveBreakpoint from 'hooks/useAboveBreakpoint';
import { useLocation } from 'react-router';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

const DesignerArchive = ({ data }) => {
    /**
     *  Init
     */

    const { t } = useTranslation();
    const location = useLocation();
    const isAboveMd = useAboveBreakpoint('md');
    const query = extractQueryParams(location.search);
    const appID = useSelector(state => state.application.applicationId);

    // Initial archive settings
    const pageSize = 9;
    const [state, setState] = useState({
        designers: [],
        hits: { visible: 0, total: 0 },
        loading: false,
        page: query.page ? parseInt(query.page, 10) : 1,
        pages: [],
    });

    // Extract and transform data
    const { breadcrumbs, hero } = data;
    const { background_media_flexible: backgroundObject, heading, paragraph } = hero?.data || {};
    const { mobile, desktop } = transformFlexibleBackground(backgroundObject);

    /**
     *  Helper functions
     */

    // Scroll to designers
    const designersRef = useRef(null);
    const scrollToDesigners = () => {
        designersRef.current &&
            scrollTo({
                // -48 to algin properly with the top
                top: designersRef.current.offsetTop - 48,
            });
    };

    // Handle pagination
    const handlePagination = (e, page) => {
        setState(prevState => ({
            ...prevState,
            page: page.name,
        }));
        scrollToDesigners();
    };

    /**
     *  Search/Filter handler
     */

    // Algolia filter search which returns an array with designers
    useEffect(() => {
        setState(prevState => ({ ...prevState, loading: true }));

        const allFilters = [
            // Add application id to get the correct market
            [`application_id: ${appID}`],
            // Only get categories with the type designer
            ['type: designer'],
        ];

        const algoliaIndex = 'product_categories'; // Will use REACT_APP_ALGOLIA_PREFIX to create something like -> 'dev_product_categories'
        const searchstring = ''; // Empty searchstring = show all

        Search(algoliaIndex, searchstring, {
            filters: allFilters,
            hitsPerPage: pageSize,
            page: state.page,
        }).then(response => {
            // Update state
            if (response) {
                const result = response.results?.[0] || [];
                const newDesigners = result.hits.map(transformDesignerCategory);
                const currentPage = result.page + 1;

                setState(prevState => ({
                    ...prevState,
                    designers: newDesigners,
                    hits: { visible: result.hits.length, total: result.nbHits },
                    loading: false,
                    page: currentPage,
                    pages: createPaginationPages(currentPage, result.nbPages, location),
                }));
            }
        });
    }, [state.page]);

    return (
        <>
            <Hero
                background={isAboveMd ? desktop : mobile}
                breadcrumbs={breadcrumbs}
                heroDescription={paragraph}
                heroTitle={heading}
                marginBottom={['32px', '40px', null, '64px']}
            />
            <ListingWrapper ref={designersRef}>
                {state.hits.total > 0 ? (
                    <DesignerGridLayout designers={state.designers} />
                ) : (
                    <NoResultComponent
                        text={state.loading ? '' : t('designers.no_designers_found')}
                        loading={state.loading}
                    />
                )}
                {state.pages.length > 1 && (
                    <Pagination
                        currentIndex={state.page - 1}
                        handleClick={handlePagination}
                        maxSiblings={isAboveMd ? 2 : 1}
                        message={t('pagination.designers_pagination')}
                        mt={['56px', null, null, '112px']}
                        pageItemCount={state.hits.visible}
                        pageSize={pageSize}
                        pages={state.pages}
                        totalItemCount={state.hits.total}
                    />
                )}
            </ListingWrapper>
        </>
    );
};

DesignerArchive.propTypes = {
    data: PropTypes.object.isRequired,
};

export default DesignerArchive;
